import React, { useState, useEffect } from 'react';
import { vehicleRegistrationAPI, authAPI, unitPermissionAPI } from '../services/api';
import ExcelJS from 'exceljs';
import Swal from 'sweetalert2';
import './UserManagement.css';

const UserManagement = ({ user }) => {
  const [users, setUsers] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    role: 'unit',
    unitId: '',
    permittedUnitIds: [] // Thêm field cho quyền xem báo cáo
  });
  const [searchUnit, setSearchUnit] = useState('');
  const [showUnitDropdown, setShowUnitDropdown] = useState(false);
  const [filteredUnits, setFilteredUnits] = useState([]);
  const [searchUser, setSearchUser] = useState('');
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [selectedPermittedUnits, setSelectedPermittedUnits] = useState([]);
  const [searchPermittedUnit, setSearchPermittedUnit] = useState('');
  const [showPermittedUnitDropdown, setShowPermittedUnitDropdown] = useState(false);
  const [filteredPermittedUnits, setFilteredPermittedUnits] = useState([]);

  useEffect(() => {
    fetchUsers();
    fetchUnits();
  }, []);

  // Filter units for search
  useEffect(() => {
    if (searchUnit.trim() === '') {
      setFilteredUnits(units);
    } else {
      const filtered = units.filter(unit =>
        unit.name.toLowerCase().includes(searchUnit.toLowerCase())
      );
      setFilteredUnits(filtered);
    }
  }, [units, searchUnit]);

  // Filter users for search
  useEffect(() => {
    if (searchUser.trim() === '') {
      setFilteredUsers(users);
    } else {
      const filtered = users.filter(user =>
        user.username.toLowerCase().includes(searchUser.toLowerCase()) ||
        (user.unit?.name && user.unit.name.toLowerCase().includes(searchUser.toLowerCase())) ||
        (user.role === 'admin' && 'quản trị viên'.includes(searchUser.toLowerCase())) ||
        (user.role === 'document' && 'quản lý tài liệu'.includes(searchUser.toLowerCase())) ||
        (user.role === 'unit' && 'thành viên'.includes(searchUser.toLowerCase()))
      );
      setFilteredUsers(filtered);
    }
  }, [users, searchUser]);

  // Update search text when unitId changes
  useEffect(() => {
    if (formData.unitId && units.length > 0) {
      const selectedUnit = units.find(unit => unit.id === parseInt(formData.unitId));
      if (selectedUnit && searchUnit !== selectedUnit.name) {
        setSearchUnit(selectedUnit.name);
      }
    } else if (!formData.unitId) {
      setSearchUnit('');
    }
  }, [formData.unitId, units]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await authAPI.getAllUsers();
      setUsers(response.data.users);
    } catch (error) {
      setMessage('Lỗi khi tải danh sách người dùng');
    } finally {
      setLoading(false);
    }
  };

  const fetchUnits = async () => {
    try {
      const response = await vehicleRegistrationAPI.getUnits();
      setUnits(response.data.units);
    } catch (error) {
      // Error fetching units
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleUnitSearch = (e) => {
    setSearchUnit(e.target.value);
    setShowUnitDropdown(true);
  };

  const handleUnitSelect = (unit) => {
    setFormData(prev => ({
      ...prev,
      unitId: unit.id
    }));
    setSearchUnit(unit.name);
    setShowUnitDropdown(false);
  };

  const handleUnitInputFocus = () => {
    setShowUnitDropdown(true);
  };

  const handleUnitInputBlur = () => {
    // Delay để cho phép click vào dropdown
    setTimeout(() => setShowUnitDropdown(false), 200);
  };

  // Xử lý chọn đơn vị có quyền xem báo cáo
  const handlePermittedUnitAdd = (unit) => {
    if (!selectedPermittedUnits.find(u => u.id === unit.id)) {
      const newSelectedUnits = [...selectedPermittedUnits, unit];
      setSelectedPermittedUnits(newSelectedUnits);
      setFormData(prev => ({
        ...prev,
        permittedUnitIds: newSelectedUnits.map(u => u.id)
      }));
    }
    setSearchPermittedUnit('');
    setShowPermittedUnitDropdown(false);
  };

  const handlePermittedUnitRemove = (unitId) => {
    const newSelectedUnits = selectedPermittedUnits.filter(u => u.id !== unitId);
    setSelectedPermittedUnits(newSelectedUnits);
    setFormData(prev => ({
      ...prev,
      permittedUnitIds: newSelectedUnits.map(u => u.id)
    }));
  };

  const handlePermittedUnitSearch = (e) => {
    const value = e.target.value;
    setSearchPermittedUnit(value);

    if (value.trim()) {
      const filtered = units.filter(unit =>
        unit.name.toLowerCase().includes(value.toLowerCase()) &&
        !selectedPermittedUnits.find(selected => selected.id === unit.id)
      );
      setFilteredPermittedUnits(filtered);
    } else {
      setFilteredPermittedUnits([]);
    }
  };

  const handlePermittedUnitInputFocus = () => {
    setShowPermittedUnitDropdown(true);
    if (!searchPermittedUnit.trim()) {
      const availableUnits = units.filter(unit =>
        !selectedPermittedUnits.find(selected => selected.id === unit.id)
      );
      setFilteredPermittedUnits(availableUnits);
    }
  };

  const handlePermittedUnitInputBlur = () => {
    setTimeout(() => setShowPermittedUnitDropdown(false), 200);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      let userId;

      if (editingUser) {
        // Cập nhật user
        await authAPI.updateUser(editingUser.id, formData);
        userId = editingUser.id;
        setMessage('Cập nhật người dùng thành công!');
      } else {
        // Tạo user mới
        const response = await authAPI.createUser(formData);
        userId = response.data.user.id;
        setMessage('Tạo người dùng thành công!');
      }

      // Nếu là supervisor, cập nhật phân quyền (kể cả khi không có phân quyền nào)
      if (formData.role === 'supervisor') {
        try {
          await unitPermissionAPI.updateSupervisorPermissions(userId, {
            unitIds: formData.permittedUnitIds || []
          });
        } catch (permissionError) {
          setMessage('Tạo/cập nhật user thành công nhưng có lỗi khi cập nhật phân quyền');
        }
      }

      // Reset form và refresh danh sách
      setFormData({
        username: '',
        password: '',
        role: 'unit',
        unitId: '',
        permittedUnitIds: []
      });
      setSearchUnit('');
      setSelectedPermittedUnits([]);
      setSearchPermittedUnit('');
      setShowCreateForm(false);
      setEditingUser(null);
      await fetchUsers();
    } catch (error) {
      setMessage('Lỗi: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async (userToEdit) => {
    setEditingUser(userToEdit);

    let permittedUnits = [];
    let permittedUnitIds = [];

    // Nếu là supervisor, load thông tin phân quyền
    if (userToEdit.role === 'supervisor') {
      try {
        const response = await unitPermissionAPI.getSupervisorPermissions(userToEdit.id);
        const permissions = response.data.permissions || [];
        permittedUnits = permissions.map(p => p.permittedUnit);
        permittedUnitIds = permittedUnits.map(u => u.id);
      } catch (error) {
        // Lỗi khi load phân quyền
      }
    }

    setFormData({
      username: userToEdit.username,
      password: '', // Không hiển thị mật khẩu cũ
      role: userToEdit.role,
      unitId: userToEdit.unitId || '',
      permittedUnitIds: permittedUnitIds
    });
    setSelectedPermittedUnits(permittedUnits);
    setSearchPermittedUnit('');

    // Set search unit name if editing unit user
    if (userToEdit.unit) {
      setSearchUnit(userToEdit.unit.name);
    } else {
      setSearchUnit('');
    }
    setShowCreateForm(true);
  };

  const handleToggleActive = async (userId, isActive) => {
    try {
      setLoading(true);
      await authAPI.toggleUserActive(userId, !isActive);
      setMessage(`${isActive ? 'Vô hiệu hóa' : 'Kích hoạt'} người dùng thành công!`);
      await fetchUsers();
    } catch (error) {
      setMessage('Lỗi khi thay đổi trạng thái người dùng');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (userId, username) => {
    const result = await Swal.fire({
      icon: 'warning',
      title: 'Xác nhận xóa',
      html: `Bạn có chắc chắn muốn xóa người dùng "<strong>${username}</strong>"?<br><br><small>Hành động này không thể hoàn tác!</small>`,
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Xóa',
      cancelButtonText: 'Hủy'
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      setLoading(true);
      await authAPI.deleteUser(userId);
      setMessage('Xóa người dùng thành công!');
      await fetchUsers();
      
      Swal.fire({
        icon: 'success',
        title: 'Thành công!',
        text: 'Xóa người dùng thành công',
        timer: 2000,
        showConfirmButton: false
      });
    } catch (error) {
      setMessage('Lỗi: ' + (error.response?.data?.message || error.message));
      Swal.fire({
        icon: 'error',
        title: 'Lỗi!',
        text: error.response?.data?.message || error.message,
        confirmButtonColor: '#1e3a8a'
      });
    } finally {
      setLoading(false);
    }
  };

  const cancelEdit = () => {
    setEditingUser(null);
    setShowCreateForm(false);
    setFormData({
      username: '',
      password: '',
      role: 'unit',
      unitId: '',
      permittedUnitIds: []
    });
    setSearchUnit('');
    setSelectedPermittedUnits([]);
    setSearchPermittedUnit('');
  };

  // Hàm xuất Excel danh sách người dùng
  const exportToExcel = async () => {
    if (!users || users.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Không có dữ liệu',
        text: 'Không có dữ liệu để xuất',
        confirmButtonColor: '#1e3a8a'
      });
      return;
    }

    try {
      // Tạo workbook với ExcelJS
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Danh sách người dùng');

      // Thiết lập độ rộng cột
      worksheet.columns = [
        { width: 30 },  // Tên đăng nhập
        { width: 20 },  // Mật khẩu
        { width: 40 }   // Đơn vị
      ];

      let currentRow = 1;

      // Tiêu đề báo cáo
      const titleCell = worksheet.getCell(currentRow, 1);
      titleCell.value = 'DANH SÁCH NGƯỜI DÙNG HỆ THỐNG';
      worksheet.mergeCells(currentRow, 1, currentRow, 3);
      titleCell.font = { name: 'Arial', size: 18, bold: true, color: { argb: 'FFFFFFFF' } };
      titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1E3A8A' } };
      titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
      titleCell.border = {
        top: { style: 'thin' }, bottom: { style: 'thin' },
        left: { style: 'thin' }, right: { style: 'thin' }
      };
      worksheet.getRow(currentRow).height = 45;
      currentRow += 2; // Bỏ qua dòng trống

      // Header bảng
      const headers = ['Tên đăng nhập', 'Mật khẩu', 'Đơn vị'];
      const headerRow = worksheet.getRow(currentRow);

      headers.forEach((header, index) => {
        const cell = headerRow.getCell(index + 1);
        cell.value = header;
        cell.font = { name: 'Arial', size: 14, bold: true, color: { argb: 'FFFFFFFF' } };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1E3A8A' } };
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
        cell.border = {
          top: { style: 'thin' }, bottom: { style: 'thin' },
          left: { style: 'thin' }, right: { style: 'thin' }
        };
      });
      headerRow.height = 40;
      currentRow++;

      // Dữ liệu người dùng
      users.forEach((userItem, index) => {
        const dataRow = worksheet.getRow(currentRow);

        // Tên đăng nhập
        dataRow.getCell(1).value = userItem.username;

        // Mật khẩu (để trống)
        dataRow.getCell(2).value = '';

        // Đơn vị
        dataRow.getCell(3).value = userItem.unit?.name || '';

        // Định dạng dòng dữ liệu
        dataRow.eachCell((cell, colNumber) => {
          cell.border = {
            top: { style: 'thin' }, bottom: { style: 'thin' },
            left: { style: 'thin' }, right: { style: 'thin' }
          };

          cell.font = { name: 'Arial', size: 12 };

          if (colNumber === 1 || colNumber === 3) {
            // Tên đăng nhập và đơn vị căn trái
            cell.alignment = { horizontal: 'left', vertical: 'middle' };
          } else {
            // Cột mật khẩu căn giữa
            cell.alignment = { horizontal: 'center', vertical: 'middle' };
          }

          // Màu nền xen kẽ
          if (index % 2 === 0) {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8F9FA' } };
          } else {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFFFF' } };
          }
        });

        dataRow.height = 35;
        currentRow++;
      });

      // Tạo tên file
      const now = new Date();
      const fileName = `danh-sach-nguoi-dung-${now.getFullYear()}-${(now.getMonth() + 1).toString().padStart(2, '0')}-${now.getDate().toString().padStart(2, '0')}.xlsx`;

      // Xuất file
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.click();
      window.URL.revokeObjectURL(url);

      Swal.fire({
        icon: 'success',
        title: 'Thành công!',
        text: 'Xuất file Excel thành công',
        timer: 2000,
        showConfirmButton: false
      });
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Lỗi!',
        text: 'Lỗi khi xuất file Excel',
        confirmButtonColor: '#1e3a8a'
      });
    }
  };

  return (
    <div className="user-management">
      <div className="user-management-header">
        <h2>Quản lý người dùng</h2>
        <div className="header-actions">
          <div className="search-container">
            <input
              type="text"
              placeholder="Tìm kiếm người dùng..."
              value={searchUser}
              onChange={(e) => setSearchUser(e.target.value)}
              className="search-input"
            />
          </div>
          <button
            className="btn-export"
            onClick={exportToExcel}
            disabled={loading || !users || users.length === 0}
            title="Xuất danh sách người dùng Excel"
          >
            Xuất Excel
          </button>
          <button
            className="btn-primary"
            onClick={() => setShowCreateForm(true)}
            disabled={loading}
          >
            Tạo người dùng mới
          </button>
        </div>
      </div>

      {message && (
        <div className={`message ${message.includes('thành công') ? 'success' : 'error'}`}>
          {message}
        </div>
      )}

      {/* Form tạo/sửa user */}
      {showCreateForm && (
        <div className="user-form-container">
          <form onSubmit={handleSubmit} className="user-form">
            <h3>{editingUser ? 'Chỉnh sửa người dùng' : 'Tạo người dùng mới'}</h3>

            <div className="form-row">
              <div className="form-group">
                <label>Tên đăng nhập:</label>
                <input
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleInputChange}
                  required
                  disabled={editingUser} // Không cho sửa username
                />
              </div>

              <div className="form-group">
                <label>Mật khẩu:</label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required={!editingUser}
                  placeholder={editingUser ? 'Để trống nếu không đổi mật khẩu' : ''}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Vai trò:</label>
                <select
                  name="role"
                  value={formData.role}
                  onChange={handleInputChange}
                  required
                >
                  <option value="unit">Thành viên</option>
                  <option value="supervisor">Giám sát viên</option>
                  <option value="admin">Quản trị viên</option>
                  <option value="document">Quản lý tài liệu</option>
                </select>
              </div>

              {(formData.role === 'unit' || formData.role === 'supervisor') && (
                <div className="form-group">
                  <label>Đơn vị:</label>
                  <div className="searchable-select">
                    <input
                      type="text"
                      placeholder="Tìm kiếm đơn vị..."
                      value={searchUnit}
                      onChange={handleUnitSearch}
                      onFocus={handleUnitInputFocus}
                      onBlur={handleUnitInputBlur}
                      required
                    />
                    {showUnitDropdown && filteredUnits.length > 0 && (
                      <div className="dropdown-list">
                        {filteredUnits.map(unit => (
                          <div
                            key={unit.id}
                            className="dropdown-item"
                            onClick={() => handleUnitSelect(unit)}
                          >
                            {unit.name}
                          </div>
                        ))}
                      </div>
                    )}
                    {showUnitDropdown && filteredUnits.length === 0 && searchUnit && (
                      <div className="dropdown-list">
                        <div className="dropdown-item no-results">
                          Không tìm thấy đơn vị
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Phần chọn quyền xem báo cáo cho supervisor */}
            {formData.role === 'supervisor' && (
              <div className="form-row">
                <div className="form-group full-width">
                  <label>Quyền xem báo cáo của các đơn vị:</label>

                  {/* Hiển thị các đơn vị đã chọn */}
                  {selectedPermittedUnits.length > 0 && (
                    <div className="selected-units">
                      {selectedPermittedUnits.map(unit => (
                        <div key={unit.id} className="selected-unit-tag">
                          <span>{unit.name}</span>
                          <button
                            type="button"
                            onClick={() => handlePermittedUnitRemove(unit.id)}
                            className="remove-unit-btn"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Input để thêm đơn vị mới */}
                  <div className="searchable-select">
                    <input
                      type="text"
                      placeholder="Tìm kiếm để thêm đơn vị..."
                      value={searchPermittedUnit}
                      onChange={handlePermittedUnitSearch}
                      onFocus={handlePermittedUnitInputFocus}
                      onBlur={handlePermittedUnitInputBlur}
                    />
                    {showPermittedUnitDropdown && filteredPermittedUnits.length > 0 && (
                      <div className="dropdown-list">
                        {filteredPermittedUnits.map(unit => (
                          <div
                            key={unit.id}
                            className="dropdown-item"
                            onClick={() => handlePermittedUnitAdd(unit)}
                          >
                            {unit.name}
                          </div>
                        ))}
                      </div>
                    )}
                    {showPermittedUnitDropdown && filteredPermittedUnits.length === 0 && searchPermittedUnit && (
                      <div className="dropdown-list">
                        <div className="dropdown-item no-results">
                          Không tìm thấy đơn vị
                        </div>
                      </div>
                    )}
                  </div>

                  <small className="form-help">
                    Chọn các đơn vị mà giám sát viên này có thể xem báo cáo. Để trống nếu chỉ xem báo cáo của đơn vị mình.
                  </small>
                </div>
              </div>
            )}

            <div className="form-actions">
              <button
                type="submit"
                disabled={loading}
                className="btn-primary"
              >
                {loading ? 'Đang lưu...' : (editingUser ? 'Cập nhật' : 'Tạo mới')}
              </button>
              <button
                type="button"
                onClick={cancelEdit}
                className="btn-secondary"
              >
                Hủy
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Danh sách users */}
      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>Tên đăng nhập</th>
              <th>Vai trò</th>
              <th>Đơn vị</th>
              <th>Trạng thái</th>
              <th>Ngày tạo</th>
              <th>Thao tác</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map(userItem => (
              <tr key={userItem.id}>
                <td>{userItem.username}</td>
                <td>
                  <span className={`role-badge ${userItem.role}`}>
                    {userItem.role === 'admin' ? 'Quản trị viên' :
                     userItem.role === 'document' ? 'Quản lý tài liệu' :
                     userItem.role === 'supervisor' ? 'Giám sát viên' :
                     'Thành viên'}
                  </span>
                </td>
                <td>{userItem.unit?.name || 'N/A'}</td>
                <td>
                  <span className={`status-badge ${userItem.isActive ? 'active' : 'inactive'}`}>
                    {userItem.isActive ? 'Hoạt động' : 'Vô hiệu hóa'}
                  </span>
                </td>
                <td>{new Date(userItem.createdAt).toLocaleDateString('vi-VN')}</td>
                <td>
                  <div className="action-buttons">
                    <button
                      onClick={() => handleEdit(userItem)}
                      className="btn-edit"
                      disabled={loading}
                    >
                      Sửa
                    </button>
                    <button
                      onClick={() => handleToggleActive(userItem.id, userItem.isActive)}
                      className={`btn-toggle ${userItem.isActive ? 'deactivate' : 'activate'}`}
                      disabled={loading || userItem.id === user.id} // Không cho tự vô hiệu hóa chính mình
                    >
                      {userItem.isActive ? 'Vô hiệu hóa' : 'Kích hoạt'}
                    </button>
                    <button
                      onClick={() => handleDelete(userItem.id, userItem.username)}
                      className="btn-delete"
                      disabled={loading || userItem.id === user.id} // Không cho tự xóa chính mình
                    >
                      Xóa
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {loading && <div className="loading">Đang xử lý...</div>}
    </div>
  );
};

export default UserManagement;
